import { Auth, Amplify } from 'aws-amplify';
import { getClientAuthToken, getUser, setCognitoId } from './user';
import { logEvent, MIXPANEL_EVENTS, onlyIdentifyUser } from '../libs/mixpanel';
import { AlertError, AlertSuccess } from '../components/Alerts';
import { Logger } from '../libs/logger';
import { API_NAME, REGISTERATION_PASSWORD } from '../constants';
import { onError } from '../libs/errorLib';
import { isSuperAdmin, removeAllExistingPoups } from '../libs/authentication.lib';
import { isFunction } from '../utils/dataType.util';
import { voidFunction } from '../utils';
import { fireAppEvent } from '../utils/emitter.util';
import { clientAuthContext } from '../libs/auth.context';

const forgotPassword = async (email) => { await Auth.forgotPassword(email); };

const forgotPasswordSubmit = async ({
  email, authCode, password, history,
}) => {
  await Auth.forgotPasswordSubmit(email, authCode, password)
    .then(async () => {
      history.push('/login');
    });
};

// Signup with Email to send the verification link
const signUp = async ({
  username, password, enableAutoSignIn = false, attributes = {},
}) => Auth.signUp({
  username,
  password,
  attributes,
  autoSignIn: { // optional - enables auto sign in after user is confirmed
    enabled: enableAutoSignIn,
  },
});

const getCurrentCreds = () => Auth.currentUserInfo();

// TODO: uncomment to send an adhoc invitation email
// await signUp(email, REGISTERATION_PASSWORD);
const handleLoginAndRedirect = async (email, password) => Auth.signIn(email, password)
  .then(async (d) => {
    const currentCreds = await getCurrentCreds();
    Logger.log('Current Creds -',d,  currentCreds);
    onlyIdentifyUser(email);
    logEvent(MIXPANEL_EVENTS['Sign In'], {}, { property: '#totalSignedIn', value: 1 });
    return {
      identityId: email, routeToLand: isSuperAdmin(email) ? '/admin' : '/',
    };
  });

// First time sign in for invited user
const firstTimeSignIn = async (email, newPassword) => Auth.signIn(email, REGISTERATION_PASSWORD)
  .then(async (user) => {
    await Auth.changePassword(user, REGISTERATION_PASSWORD, newPassword);
    return user;
  }).then(async () => {
    await setCognitoId({ email });
    onlyIdentifyUser(email);
    logEvent(MIXPANEL_EVENTS['User Verified'], { email });
    logEvent(MIXPANEL_EVENTS['Sign Up'], { email });
    return email;
  }).catch((error) => {
    Logger.log('ERR-', error);
    logEvent(MIXPANEL_EVENTS['Sign Up Failed'], { email, time: Date.now(), error: error.message });
    onError(new Error(`${email} failed to sign up due to ${error.message}`));
    AlertError({ head: 'Sign up failed, please contact IdentXLabs admin' });
    throw new Error(error.message);
  });

const confirmSignUp = async (email, authCode) => {
  await Auth.confirmSignUp(email, authCode).then(() => {
    if (typeof window.gr === 'function') {
      // 👇️ this runs
      Logger.log('✅ gr function is defined');

      window.gr('track', 'conversion', { email });
    } else {
      Logger.log('⛔️ gr function is NOT defined'); // Normally this means the gr function wasn't declared here, try and look why the Tracking Script isn't running on this page
    }
  });
};

const changePassword = async (oldPass, newPass) => {
  await Auth.currentAuthenticatedUser()
    .then((user) => Auth.changePassword(user, oldPass, newPass));
};

// Sign out user from application
const signOut = async (global = false, callback = () => { /* do something */ }) => Auth.signOut({ global })
  .catch((error) => {
    Logger.error('ERROR ON SIGNOUT - ', error);
  })
  .finally(() => {
    removeAllExistingPoups();
    callback && callback();
  });

// Getting fresh session at the start of application
const hasActiveSession = async (onSuccessCallback, onFailureCallback) => Auth.currentSession().then(() => {
  isFunction(onSuccessCallback) && onSuccessCallback();
}).catch((e) => signOut(true, () => isFunction(onFailureCallback) && onFailureCallback(e.message)));

// Checking and then getting fresh session once inside the  application
const getSessionWithinApp = async (onSuccessCallback = voidFunction) => Auth.currentSession().then(() => {
  onSuccessCallback();
}).catch(async () => {
  signOut(true, () => fireAppEvent('resetStore'));
});

// Send the confirmation link or code for signup
const resendConfirmationCode = async (email, clientMetadata) => {
  try {
    await Auth.resendSignUp(email, clientMetadata);
    AlertSuccess({
      head: 'Verification Link Sent',
    });
  } catch (err) {
    AlertError({
      head: 'Something went wrong',
    });
  }
};

const configureAWSAmplify = () => {
  Amplify.configure({
    Auth: {
      mandatorySignIn: true,
      signUpVerificationMethod: 'code',
      region: process.env.REACT_APP_REGION,
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
      identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
      userPoolWebClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
    },
    Storage: {
      bucket: process.env.REACT_APP_S3_ATTACHMENT_BUCKET_NAME,
      region: process.env.REACT_APP_REGION,
      // identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
    },
    API: {
      endpoints: [
        {
          name: API_NAME,
          endpoint: process.env.REACT_APP_API_BASE_URL,
          region: process.env.REACT_APP_REGION,
        },
      ],
    },
  });
  Logger.log('Configured Amplify')
};

export const getUserDetails = async (id) => {
  try {
    const { token } = await getClientAuthToken();
    clientAuthContext.setToken(token);
    const userData = await getUser({ id });
    return {
        user: {
          id,
          ...userData,
        },
        token: token,
      };
  } catch (error) {
    return ({ error: error.message });
  }  
};

export {
  forgotPassword, handleLoginAndRedirect, confirmSignUp, signUp, resendConfirmationCode,
  forgotPasswordSubmit, hasActiveSession, configureAWSAmplify, changePassword,
  signOut, getCurrentCreds, getSessionWithinApp, firstTimeSignIn,
};
